import * as Dialog from '@radix-ui/react-dialog';
import { Pause, Play, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { SubscriptionStatusSchema } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { useReactivateSubscription, useSuspendSubscription } from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';

interface SubscriptionActionsProps {
  subscriptionId: string;
  status: SubscriptionStatusSchema;
}

const SUSPENDABLE_STATUS = new Set<SubscriptionStatusSchema>([
  'ativa',
  'em_teste',
  'inadimplente',
  'aguardando_pagamento',
]);

const REACTIVATABLE_STATUS = new Set<SubscriptionStatusSchema>(['suspensa', 'cancelada']);

export function SubscriptionActions({
  subscriptionId,
  status,
}: SubscriptionActionsProps): JSX.Element | null {
  const canSuspend = SUSPENDABLE_STATUS.has(status);
  const canReactivate = REACTIVATABLE_STATUS.has(status);

  if (!canSuspend && !canReactivate) return null;

  return (
    <div className="flex items-center gap-2">
      {canSuspend && <SuspendButton subscriptionId={subscriptionId} />}
      {canReactivate && <ReactivateButton subscriptionId={subscriptionId} />}
    </div>
  );
}

function SuspendButton({ subscriptionId }: { subscriptionId: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const suspend = useSuspendSubscription();
  const reasonValid = reason.trim().length >= 1 && reason.length <= 500;

  async function handleSubmit(): Promise<void> {
    if (!reasonValid) return;
    try {
      await suspend.mutateAsync({ id: subscriptionId, payload: { reason: reason.trim() } });
      toast.success('Assinatura suspensa.');
      setOpen(false);
      setReason('');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos suspender.'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <PillButton type="button" icon={<Pause size={12} strokeWidth={2} />}>
          Suspender
        </PillButton>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(520px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel shadow-lg p-6',
            'focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <Dialog.Title className="text-[18px] font-semibold text-ink">
              Suspender assinatura
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <p className="mb-4 text-[12px] text-muted">
            Suspender pausa a cobrança recorrente sem cancelar. Você pode reativar a qualquer
            momento.
          </p>

          <Field
            label="Motivo"
            required
            error={
              reason.length > 0 && !reasonValid
                ? reason.length > 500
                  ? 'Máximo 500 caracteres.'
                  : 'Mínimo 1 caractere.'
                : undefined
            }
          >
            {({ inputId }) => (
              <Textarea
                id={inputId}
                rows={3}
                placeholder="Cliente solicitou pausa, chargeback temporário, etc."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>

          <div className="mt-5 flex items-center justify-end gap-2 border-t border-line pt-4">
            <PillButton type="button" onClick={() => setOpen(false)} disabled={suspend.isPending}>
              Cancelar
            </PillButton>
            <PillButton
              solid
              type="button"
              onClick={() => void handleSubmit()}
              disabled={!reasonValid || suspend.isPending}
            >
              {suspend.isPending ? 'Suspendendo…' : 'Suspender'}
            </PillButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ReactivateButton({ subscriptionId }: { subscriptionId: string }): JSX.Element {
  const reactivate = useReactivateSubscription();
  const [confirming, setConfirming] = useState(false);

  async function handleConfirm(): Promise<void> {
    try {
      await reactivate.mutateAsync({ id: subscriptionId, payload: {} });
      toast.success('Assinatura reativada.');
      setConfirming(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos reativar.'));
    }
  }

  return (
    <Dialog.Root open={confirming} onOpenChange={setConfirming}>
      <Dialog.Trigger asChild>
        <PillButton solid type="button" icon={<Play size={12} strokeWidth={2} />}>
          Reativar
        </PillButton>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(480px,calc(100vw-32px))] rounded-[14px] border border-line bg-panel shadow-lg p-6',
            'focus:outline-none',
          )}
        >
          <Dialog.Title className="text-[18px] font-semibold text-ink">
            Reativar assinatura?
          </Dialog.Title>
          <p className="mt-2 text-[12px] text-muted">
            Status volta pra ativa. Preço é preservado (grandfathered) e a próxima cobrança é
            recalculada a partir de hoje.
          </p>
          <div className="mt-5 flex items-center justify-end gap-2 border-t border-line pt-4">
            <PillButton
              type="button"
              onClick={() => setConfirming(false)}
              disabled={reactivate.isPending}
            >
              Cancelar
            </PillButton>
            <PillButton
              solid
              type="button"
              onClick={() => void handleConfirm()}
              disabled={reactivate.isPending}
            >
              {reactivate.isPending ? 'Reativando…' : 'Reativar'}
            </PillButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
